import { useState, useEffect, useCallback } from 'react';
import { supabase, CmsBusiness } from '@/lib/supabase';
import RichEditor from '@/components/feature/RichEditor';
import ImageUploader from '@/components/base/ImageUploader';

interface EditForm {
  title: string;
  description: string;
  icon: string;
  image_url: string;
  detail_intro: string;
}

export default function BusinessCMS() {
  const [list, setList] = useState<CmsBusiness[]>([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState<number | null>(null);
  const [editData, setEditData] = useState<EditForm | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');

  const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
    setToast(msg);
    setToastType(type);
    setTimeout(() => setToast(''), 3000);
  };

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('cms_business')
        .select('*')
        .order('sort_order', { ascending: true });
      if (error) throw error;
      setList(data || []);
    } catch {
      showToast('加载失败，请刷新重试', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const handleEdit = (item: CmsBusiness) => {
    setEditId(item.id);
    setEditData({
      title: item.title,
      description: item.description || '',
      icon: item.icon || '',
      image_url: item.image_url || '',
      detail_intro: item.detail_intro || '',
    });
  };

  const handleSave = async () => {
    if (!editId || !editData) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from('cms_business')
        .update({
          title: editData.title,
          description: editData.description,
          icon: editData.icon,
          image_url: editData.image_url,
          detail_intro: editData.detail_intro,
          updated_at: new Date().toISOString(),
        })
        .eq('id', editId);
      if (error) throw error;
      showToast('保存成功');
      setEditId(null);
      setEditData(null);
      await fetchList();
    } catch {
      showToast('保存失败，请重试', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleMoveOrder = async (item: CmsBusiness, direction: 'up' | 'down') => {
    const idx = list.findIndex((b) => b.id === item.id);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= list.length) return;
    const swapItem = list[swapIdx];
    try {
      await Promise.all([
        supabase.from('cms_business').update({ sort_order: swapItem.sort_order }).eq('id', item.id),
        supabase.from('cms_business').update({ sort_order: item.sort_order }).eq('id', swapItem.id),
      ]);
      await fetchList();
    } catch {
      showToast('排序失败', 'error');
    }
  };

  const handleTogglePublish = async (item: CmsBusiness) => {
    try {
      const { error } = await supabase
        .from('cms_business')
        .update({ is_published: !item.is_published, updated_at: new Date().toISOString() })
        .eq('id', item.id);
      if (error) throw error;
      showToast(item.is_published ? '已取消发布' : '已发布');
      await fetchList();
    } catch {
      showToast('操作失败', 'error');
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-800">核心业务管理</h2>
          <p className="text-sm text-gray-400 mt-0.5">
            共 {list.length} 项业务 · {list.filter((b) => b.is_published).length} 项已发布
          </p>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-100 p-5 animate-pulse">
              <div className="flex gap-4">
                <div className="w-10 h-10 bg-gray-100 rounded-xl flex-shrink-0"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-100 rounded w-1/4"></div>
                  <div className="h-3 bg-gray-100 rounded w-3/4"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {list.map((item, idx) => (
            <div
              key={item.id}
              className={`bg-white rounded-xl border p-5 transition-all ${
                item.is_published ? 'border-gray-100' : 'border-gray-200 opacity-70'
              }`}
            >
              {editId === item.id ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">业务名称</label>
                      <input
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500"
                        value={editData?.title || ''}
                        onChange={(e) => setEditData((d) => d ? { ...d, title: e.target.value } : d)}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">图标类名 (Remix Icon)</label>
                      <input
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500"
                        value={editData?.icon || ''}
                        onChange={(e) => setEditData((d) => d ? { ...d, icon: e.target.value } : d)}
                        placeholder="ri-server-line"
                      />
                    </div>
                  </div>
                  <ImageUploader
                    label="封面图片"
                    value={editData?.image_url || ''}
                    onChange={(url) => setEditData((d) => d ? { ...d, image_url: url } : d)}
                    placeholder="粘贴图片 URL，如 https://..."
                  />
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">业务描述</label>
                    <RichEditor
                      value={editData?.description || ''}
                      onChange={(val) => setEditData((d) => d ? { ...d, description: val } : d)}
                      placeholder="请输入业务描述，支持富文本排版..."
                      minHeight={180}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">详情页介绍</label>
                    <RichEditor
                      value={editData?.detail_intro || ''}
                      onChange={(val) => setEditData((d) => d ? { ...d, detail_intro: val } : d)}
                      placeholder="请输入详情页完整介绍内容，支持标题、图片、列表等富文本排版..."
                      minHeight={320}
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="px-4 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 cursor-pointer whitespace-nowrap disabled:opacity-50"
                    >
                      {saving ? '保存中...' : '保存'}
                    </button>
                    <button
                      onClick={() => { setEditId(null); setEditData(null); }}
                      className="px-4 py-2 bg-gray-100 text-gray-600 text-sm rounded-lg hover:bg-gray-200 cursor-pointer whitespace-nowrap"
                    >
                      取消
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-teal-50 rounded-xl flex-shrink-0">
                    <i className={`${item.icon || 'ri-briefcase-line'} text-teal-600 text-xl`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-sm font-semibold text-gray-800">{item.title}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${item.is_published ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                        {item.is_published ? '已发布' : '草稿'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 leading-relaxed line-clamp-2">{item.description}</p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => handleMoveOrder(item, 'up')}
                      disabled={idx === 0}
                      className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                      title="上移"
                    >
                      <i className="ri-arrow-up-line text-base"></i>
                    </button>
                    <button
                      onClick={() => handleMoveOrder(item, 'down')}
                      disabled={idx === list.length - 1}
                      className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                      title="下移"
                    >
                      <i className="ri-arrow-down-line text-base"></i>
                    </button>
                    <button
                      onClick={() => handleEdit(item)}
                      className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors cursor-pointer"
                      title="编辑"
                    >
                      <i className="ri-edit-line text-base"></i>
                    </button>
                    <button
                      onClick={() => handleTogglePublish(item)}
                      className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors cursor-pointer"
                      title={item.is_published ? '取消发布' : '发布'}
                    >
                      <i className={`${item.is_published ? 'ri-eye-off-line' : 'ri-eye-line'} text-base`}></i>
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {toast && (
        <div className={`fixed bottom-6 right-6 text-white text-sm px-4 py-2.5 rounded-lg shadow-lg z-50 ${toastType === 'error' ? 'bg-rose-600' : 'bg-gray-800'}`}>
          <div className="flex items-center gap-2">
            <i className={`${toastType === 'error' ? 'ri-error-warning-line text-rose-200' : 'ri-checkbox-circle-line text-emerald-400'}`}></i>
            {toast}
          </div>
        </div>
      )}
    </div>
  );
}
